import { cn } from "@tcg/ui";
import { Mail, Phone, Search } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import type { PickupFind } from "@/lib/types";

export type ContactKind = "email" | "phone";

/** Full-width digits / letters typed on a Japanese keyboard → the plain ones (order-service
 * compares NFKC too); spaces around removed. */
export function normalizeTyped(value: string): string {
  return value.normalize("NFKC").trim();
}

/** order-service matches a phone digit by digit and needs at least this many (T05-30
 * `MIN_PHONE_DIGITS`) — a shorter entry would only use up one of the customer's tries. */
export const MIN_PHONE_DIGITS = 8;

/** The body of `POST /pos/pickups/find` — or null while something is missing. */
export function findBody(
  orderNumber: string,
  kind: ContactKind,
  contact: string,
): PickupFind | null {
  const number = normalizeTyped(orderNumber).toUpperCase();
  const value = normalizeTyped(contact);
  if (number.length < 3) {
    return null;
  }
  if (kind === "email") {
    return value.length >= 3 && value.includes("@") ? { orderNumber: number, email: value } : null;
  }
  return value.replace(/\D/g, "").length >= MIN_PHONE_DIGITS
    ? { orderNumber: number, phone: value }
    : null;
}

/**
 * 受取 without the QR (T07-42, T05-30): the customer forgot the QR / has no phone with them → the
 * order number (in the order e-mail) + the e-mail **or** phone of the order. Only orders to be
 * picked up at this shop are found; a wrong detail answers "not found" (nothing says whether the
 * order exists) and too many tries wait a while — order-service decides both.
 */
export function FindByNumber({
  onFind,
  busy,
  initialNumber = "",
}: {
  onFind: (body: PickupFind) => void;
  busy: boolean;
  initialNumber?: string;
}) {
  const { t } = useTranslation();
  const [orderNumber, setOrderNumber] = useState(initialNumber);
  const [kind, setKind] = useState<ContactKind>("email");
  const [contact, setContact] = useState("");
  const body = findBody(orderNumber, kind, contact);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body && !busy) {
      onFind(body);
    }
  };

  return (
    <form
      aria-label={t("pickup.find_title")}
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-md border p-3"
    >
      <p className="text-muted-foreground">{t("pickup.find_help")}</p>
      <label className="flex flex-col gap-1">
        <span className="font-medium">{t("pickup.find_number")}</span>
        <input
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="S-2026-000123"
          className="min-h-pos-touch rounded-md border px-3 font-mono text-lg"
        />
      </label>
      {/* native radios: arrow keys move the choice, one tab stop (a11y) — styled as buttons */}
      <fieldset className="flex gap-2">
        <legend className="sr-only">{t("pickup.find_contact")}</legend>
        {(
          [
            ["email", Mail],
            ["phone", Phone],
          ] as const
        ).map(([value, Icon]) => (
          <label
            key={value}
            className={cn(
              "relative flex min-h-pos-touch flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border text-lg has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
              kind === value && "border-primary bg-primary text-primary-foreground",
            )}
          >
            <input
              type="radio"
              name="pickup-find-contact"
              value={value}
              checked={kind === value}
              onChange={() => {
                setKind(value);
                setContact("");
              }}
              // covers the whole button (a tap anywhere is on the radio) but is not seen
              className="absolute inset-0 m-0 size-full cursor-pointer appearance-none rounded-md opacity-0"
            />
            <Icon aria-hidden className="size-5" />
            {t(`pickup.find_by_${value}`)}
          </label>
        ))}
      </fieldset>
      <label className="flex flex-col gap-1">
        <span className="font-medium">
          {kind === "email" ? t("pickup.find_email") : t("pickup.find_phone")}
        </span>
        <input
          type={kind === "email" ? "email" : "tel"}
          inputMode={kind === "email" ? "email" : "tel"}
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          aria-describedby={kind === "phone" ? "pickup-find-phone-help" : undefined}
          className="min-h-pos-touch rounded-md border px-3 text-lg"
        />
      </label>
      {kind === "phone" ? (
        <p id="pickup-find-phone-help" className="-mt-2 text-muted-foreground">
          {t("pickup.find_phone_help")}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={!body || busy}
        className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md bg-primary text-lg font-semibold text-primary-foreground disabled:opacity-50"
      >
        <Search aria-hidden className="size-5" />
        {busy ? t("app.loading") : t("pickup.find_submit")}
      </button>
    </form>
  );
}
